import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { observeChartResize } from '../chart-utils/chart-resize.util';
import { trackByIndex, withAlphaColor } from '../chart-utils/chart-legend.util';

export interface ChartDoughnutSegment {
  label: string;
  value: number;
  color: string;
}

@Component({
  selector: 'app-chart-doughnut',
  templateUrl: './chart-doughnut.component.html',
  styleUrls: ['./chart-doughnut.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChartDoughnutComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() segments: ChartDoughnutSegment[] = [];
  @Input() noDataLabel = 'Sem dados';

  isSegmentHovered = false;

  readonly trackByIndex = trackByIndex;

  private selectedSegmentIndices = new Set<number>();
  private segmentColors: string[] = [];

  legendItems: { text: string; color: string; dimmed: boolean; selected: boolean }[] = [];

  @ViewChild(BaseChartDirective) private chartDirective?: BaseChartDirective;

  doughnutChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  doughnutChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    interaction: {
      mode: 'point',
      intersect: true
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: () => '',
          label: (context) => {
            const value = context.parsed;
            const percentage = this.totalAmount > 0 ? (value / this.totalAmount) * 100 : 0;
            return `${context.label}: ${this.configurationService.formatNumberByCountry(value)} (${this.formatPercent(percentage)}%)`;
          }
        }
      }
    }
  };

  private stopResizeObserver?: () => void;
  totalAmount = 0;

  constructor(
    private translateService: TranslateService,
    private configurationService: ConfigurationService,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  ngAfterViewInit(): void {
    this.stopResizeObserver = observeChartResize(this.elementRef.nativeElement, () => this.chartDirective);
  }

  ngOnDestroy(): void {
    this.stopResizeObserver?.();
  }

  ngOnChanges(): void {
    this.totalAmount = this.segments.reduce((sum, segment) => sum + segment.value, 0);
    this.isSegmentHovered = false;
    this.buildChartData();
  }

  get hasData(): boolean {
    return this.totalAmount > 0;
  }

  get centerLabel(): string {
    if (this.selectedSegmentIndices.size === 0) return this.translateService.instant('total');
    if (this.selectedSegmentIndices.size > 1) return this.translateService.instant('selected');
    const labels = this.doughnutChartData.labels as string[] | undefined;
    const [index] = this.selectedSegmentIndices;
    return labels?.[index] ?? this.translateService.instant('total');
  }

  get centerValue(): number {
    if (this.selectedSegmentIndices.size === 0) return this.totalAmount;
    const values = this.doughnutChartData.datasets[0]?.data as number[] | undefined;
    if (!values) return this.totalAmount;
    return Array.from(this.selectedSegmentIndices).reduce((sum, i) => sum + (values[i] ?? 0), 0);
  }

  get legendFocusEnabled(): boolean {
    return this.legendItems.length > 1;
  }

  onCanvasMouseMove(event: MouseEvent): void {
    const chart = this.chartDirective?.chart;
    this.isSegmentHovered = !!chart && chart.getElementsAtEventForMode(event, 'point', { intersect: true }, false).length > 0;
  }

  onLegendItemClick(index: number): void {
    if (!this.legendFocusEnabled) return;
    const next = new Set(this.selectedSegmentIndices);
    if (next.has(index)) next.delete(index); else next.add(index);
    this.selectedSegmentIndices = next.size === this.legendItems.length ? new Set() : next;
    this.applySegmentHighlight();
  }

  private buildChartData(): void {
    this.selectedSegmentIndices = new Set();

    const visibleSegments = this.segments.filter(segment => segment.value > 0);
    this.segmentColors = visibleSegments.map(s => s.color);
    const total = visibleSegments.reduce((sum, s) => sum + s.value, 0);
    const labels = visibleSegments.map(s => s.label);

    this.doughnutChartData.labels = labels;
    this.doughnutChartData.datasets = [{
      data: visibleSegments.map(s => s.value),
      backgroundColor: [...this.segmentColors]
    }];
    this.legendItems = labels.map((label, i) => ({
      text: total > 0 ? `${label} (${this.formatPercent((visibleSegments[i].value / total) * 100)}%)` : label,
      color: this.segmentColors[i],
      dimmed: false,
      selected: false
    }));
    this.chartDirective?.chart?.update();
  }

  private formatPercent(value: number): string {
    return value.toLocaleString(this.translateService.currentLang || 'pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private applySegmentHighlight(): void {
    const dataset = this.doughnutChartData.datasets[0];
    if (!dataset || !this.segmentColors.length) return;

    const hasSelection = this.selectedSegmentIndices.size > 0;
    this.doughnutChartData.datasets = [{
      ...dataset,
      backgroundColor: this.segmentColors.map((color, i) => {
        const isSelected = !hasSelection || this.selectedSegmentIndices.has(i);
        return isSelected ? color : withAlphaColor(color, 0.25);
      })
    }];
    this.legendItems = this.legendItems.map((item, i) => ({
      ...item,
      dimmed: hasSelection && !this.selectedSegmentIndices.has(i),
      selected: this.selectedSegmentIndices.has(i)
    }));
    this.chartDirective?.chart?.update('none');
  }
}
